import React, { useMemo } from "react";
import { useEditor, EditorContent, JSONContent } from "@tiptap/react";
import Placeholder from "@tiptap/extension-placeholder";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import { PluginKey } from "@tiptap/pm/state";
import {
  Avatar,
  Box,
  Button,
  HStack,
  IconButton,
  Tooltip,
  Flex,
  VStack,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  useApiMutation,
  useApiQuery,
} from "../../../../utilities/apibelRequest";
import {
  createUserMentionExtension,
  createSectionMentionExtension,
} from "../utils/mentionEditorExtensions";
import useToast from "../../../../utilities/useToast";
import { useAppState } from "../../../../components/App/AppProvider";
import {
  SectionsMentionIcon,
  UserMentionIcon,
} from "../../../../constants/commonIcons";

const MentionEditor = () => {
  const params = useParams();
  const { manualID } = params;
  const { app } = useAppState();
  const { displayToast } = useToast();

  const userQuery = useApiQuery("user/selectable", null);
  const manualSections = useApiQuery("manual/manySectionsForSelection", {
    query: "",
    manualID,
  });
  const createCommentMutation = useApiMutation("comments/create");
  const queryClient = useQueryClient();

  const currentUserName = `${app.userInfo.firstName} ${app.userInfo.lastName}`;
  const extensions = useMemo(() => {
    const baseExtensions = [
      StarterKit,
      Placeholder.configure({
        placeholder:
          "Add a comment you can type @ to mention someone or / for sections...",
      }),
    ];

    if (userQuery.isSuccess && userQuery.data) {
      const UserMention = createUserMentionExtension(() => userQuery);
      baseExtensions.push(UserMention);
    }

    if (manualSections.isSuccess && manualSections.data) {
      const SectionMention = createSectionMentionExtension(
        () => manualSections,
      );
      baseExtensions.push(SectionMention);
    }

    return baseExtensions;
  }, [
    userQuery.isSuccess,
    userQuery.data,
    manualSections.isSuccess,
    manualSections.data,
  ]);

  const editor = useEditor(
    {
      extensions,
    },
    [extensions],
  );

  const isEditorContentEmpty = (content: JSONContent): boolean => {
    if (!content || !content.content || !Array.isArray(content.content)) {
      return true;
    }

    return content.content.every((node) => {
      if (
        node.type === "paragraph" &&
        (!node.content || node.content.length === 0)
      ) {
        return true;
      }

      if (node.type === "paragraph" && node.content) {
        return node.content.every((textNode) => {
          return (
            textNode.type === "text" &&
            (!textNode.text || textNode.text.trim() === "")
          );
        });
      }

      return false;
    });
  };

  const handleAddComment = async () => {
    if (!editor) return;
    const content = editor.getJSON();
    if (isEditorContentEmpty(content)) {
      displayToast({
        title: "Failed to add comment",
        description: "Comment content cannot be empty.",
        status: "error",
      });
      return;
    }

    try {
      await createCommentMutation.mutateAsync({
        data: {
          content: JSON.stringify(content),
          manualID: manualID || undefined,
        },
      });

      editor.commands.setContent({
        type: "doc",
        content: [
          {
            type: "paragraph",
            content: [{ type: "text", text: "" }],
          },
        ],
      });

      displayToast({
        title: "Comment Added",
        description: "Your comment has been added successfully.",
        status: "success",
      });

      queryClient.invalidateQueries({ queryKey: ["comments"] });
    } catch (error) {
      console.warn(error);
      displayToast({
        title: "Unable to add comment",
        description: "An unexpected error occurred. Please try again.",
        status: "error",
      });
    }
  };
  // Toolbar handlers
  const insertUserMention = () => {
    if (!editor) return;
    editor.commands.focus();
    editor.commands.command(({ tr, dispatch }) => {
      if (dispatch) {
        tr.insertText("@");
      }
      return true;
    });
  };

  const insertSectionMention = () => {
    if (!editor) return;
    editor.commands.focus();

    editor.commands.command(({ tr, dispatch }) => {
      if (dispatch) {
        tr.insertText("/");
      }
      return true;
    });
  };

  if (!editor) {
    return (
      <Box mx="auto" pos="relative">
        <Box
          border="1px solid"
          borderColor="gray.300"
          borderRadius="md"
          p={3}
          minH="120px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          color="gray.500">
          Loading editor...
        </Box>
      </Box>
    );
  }

  return (
    <Box w="full">
      <Box
        border="1px solid"
        borderColor="gray.300"
        borderRadius="md"
        p={4}
        minH="120px"
        bg="white"
        _focusWithin={{
          borderColor: "blue.400",
          boxShadow: "0 0 0 1px #3182ce",
        }}>
        <VStack spacing={3} align="stretch">
          {/* Header with avatar */}
          <HStack spacing={3} align="start">
            <Avatar name={currentUserName} size="sm" />
            <Box flex="1" minH="60px">
              <Box
                className="manual-rte"
                sx={{
                  ".ProseMirror": {
                    outline: "none",
                    border: "none",
                    padding: "8px 0",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    minHeight: "60px",
                    "&:focus": {
                      outline: "none",
                    },
                    p: {
                      margin: "0 0 8px 0",
                      "&:last-child": {
                        marginBottom: "0",
                      },
                    },
                    ".mention": {
                      backgroundColor: "#e2e8f0",
                      padding: "2px 4px",
                      borderRadius: "4px",
                      color: "#2d3748",
                      fontWeight: "500",
                    },
                    ".section-link": {
                      color: "#3182ce",
                      textDecoration: "underline",
                      cursor: "pointer",
                      backgroundColor: "#ebf8ff",
                      padding: "2px 4px",
                      borderRadius: "4px",
                      fontWeight: "500",
                    },
                  },
                }}>
                <EditorContent editor={editor} />
              </Box>
            </Box>
          </HStack>

          {/* Toolbar */}
          <Flex justify="space-between" align="center">
            <HStack spacing={1}>
              <Tooltip label="Mention User (@)">
                <IconButton
                  size="sm"
                  variant="ghost"
                  icon={<UserMentionIcon />}
                  onClick={insertUserMention}
                  isDisabled={!userQuery.isSuccess || !userQuery.data}
                  color="gray.600"
                  _hover={{ bg: "gray.100", color: "gray.800" }}
                  aria-label="Mention User"
                />
              </Tooltip>

              <Tooltip label="Link Section (/)">
                <IconButton
                  size="sm"
                  variant="ghost"
                  icon={<SectionsMentionIcon />}
                  onClick={insertSectionMention}
                  isDisabled={!manualSections.isSuccess || !manualSections.data}
                  color="gray.600"
                  _hover={{ bg: "gray.100", color: "gray.800" }}
                  aria-label="Link Section"
                />
              </Tooltip>
            </HStack>

            <Button
              onClick={handleAddComment}
              size="sm"
              isLoading={createCommentMutation.isPending}
              isDisabled={isEditorContentEmpty(editor.getJSON())}>
              Add Comment
            </Button>
          </Flex>
        </VStack>
      </Box>
    </Box>
  );
};

export default MentionEditor;
